import {
  type CrmObject,
  PlacetelCallDirection,
  PlacetelCallEvent,
  PlacetelCallOutcome,
  PlacetelPhoneTokenError,
} from "../../generated/graphql";
import { captureException } from "../errorHandling";
import { trace } from "../trace";
import {
  digestToken,
  getCallingInstance,
  holdTokenDigest,
  initCallingOnce,
  isCallingRegistered,
  isHeldTokenDigest,
  isTornDown,
  markCallingRegistered,
  markTornDown,
  refreshCallback,
  setRenewer,
} from "./callingSession";
import type {
  CallingCall,
  CallingInstance,
  CallingLine,
  LocalMicrophoneStream,
  SdkToken,
  WebexCallingGlobal,
} from "./webexCalling";

export type PhoneStatus =
  | { kind: "starting" }
  | { kind: "ready" }
  | { kind: "notConnected" }
  | { kind: "unavailable" }
  | { kind: "error" };

export type IssuedPhoneToken = {
  accessToken: string | null;
  expiresAt: string | null;
  errorCode: PlacetelPhoneTokenError | null;
};

export type ActiveCall = {
  direction: "outbound" | "inbound";
  /** Null for an inbound caller who withholds the number. */
  number: string | null;
  /** The remote party's name when the phone knows it; never set for outbound calls. */
  name: string | null;
  /** "connecting": an incoming call the agent answered, until the call connects. */
  phase: "dialing" | "ringing" | "connecting" | "connected";
  muted: boolean;
  /** When the call was dialled; once connected, when it connected. */
  startedAt: number;
  /** The backend's id of the call (PlacetelCall.id); null until the first call event was recorded. */
  callId: string | null;
  /** The CRM record the call is linked to: the one it was dialled from, or the agent's choice later. */
  record: DialRecord | null;
};

/** A call that ended after the backend knew it; a call the backend never recorded has none. */
export type EndedCall = {
  callId: string;
  number: string | null;
  direction: ActiveCall["direction"];
  /** Whether the call was linked to a CRM record, so its note appears there. */
  linked: boolean;
};

export type DialRecord = { object: CrmObject; salesforceId: string };

export type DialResult =
  | { ok: true }
  | { ok: false; reason: "notReady" | "inCall" | "cancelled" | "invalidNumber" | "microphone" | "failed" };

/** Why an incoming call could not be answered. */
export type AnswerResult = { ok: true } | { ok: false; reason: "noCall" | "cancelled" | "microphone" | "failed" };

export type CallEventInput = {
  portalCallId: string;
  direction: PlacetelCallDirection;
  remoteNumber: string | null;
  event: PlacetelCallEvent;
  outcome?: PlacetelCallOutcome;
  startedAt?: string;
  answeredAt?: string;
  record?: DialRecord;
};

export type RecordedCallEvent = { id: string; remoteNumber: string | null };

type PhoneEngineOptions = {
  loadCalling: () => Promise<WebexCallingGlobal>;
  /** `renew` asks for a fresh token although the cached one is still valid: the SDK holds that one. */
  issueToken: (options: { renew: boolean }) => Promise<IssuedPhoneToken>;
  /** Every end uses keepalive; `keepalive` also lets other queued events outlive a page transition. */
  recordEvent: (input: CallEventInput, options?: { keepalive: boolean }) => Promise<RecordedCallEvent>;
  onStatus: (status: PhoneStatus) => void;
  onCall: (call: ActiveCall | null) => void;
  /** Fires once when a call with a backend id ended; the engine keeps nothing of it. */
  onCallEnded: (call: EndedCall) => void;
  /** An answered incoming call did not connect in time and was ended as failed; its media never flowed. */
  onConnectFailed: () => void;
  /** The remote party's audio; null once the call ended. */
  onRemoteMedia: (track: MediaStreamTrack | null) => void;
  /** The SDK of this page was torn down and cannot start again; only a page reload brings the phone back. */
  onReloadRequired: () => void;
};

export type PhoneEngine = {
  start: () => Promise<void>;
  /** Pauses the phone: ends a running call and signs the line out; the SDK instance stays for the next start. */
  stop: () => Promise<void>;
  retry: () => Promise<void>;
  /**
   * Stops for good: also signs the SDK out. Afterwards the page has to reload to phone again.
   * `unloading` means the page is going away, so the end of a running call is sent at once, with keepalive.
   * `keepalive` sends the end of a running call with keepalive but in order, for a sign-out that reloads the page.
   * Resolves once the end of a running or just ended call was sent to the backend.
   */
  shutdown: (options?: { unloading?: boolean; keepalive?: boolean }) => Promise<void>;
  dial: (number: string, record?: DialRecord) => Promise<DialResult>;
  /** Answers the ringing incoming call. */
  answer: () => Promise<AnswerResult>;
  /** Declines the ringing incoming call; the caller hears busy. */
  decline: () => void;
  hangUp: () => void;
  setMuted: (muted: boolean) => void;
  /** Links the running call to a CRM record, replacing the one it was dialled with. */
  linkRecord: (record: DialRecord) => void;
};

/** Placeholder, not a secret: the SDK only renews a token while it holds some refresh token. */
export const PORTAL_MANAGED_REFRESH_TOKEN = "portal-managed";

const SDK_TOKEN_ERROR_TYPE = "token_error";
// The SDK itself clears an answered or connecting call that is not established after 10 s
// (call state machine, S_SEND_CALL_CONNECT and S_RECV_CALL_CONNECT). The portal waits longer, so the
// SDK's own end wins whenever it works and the portal only ends a call the SDK leaves hanging.
const CONNECT_TIMEOUT_MS = 15_000;
// The SDK renews its token on a timer that only runs again after a renewal worked (webex-core
// credentials.js, scheduleRefresh), so the engine asks it again after a failed one.
const RENEW_RETRY_MS = 60_000;
const RENEW_RETRY_LIMIT = 3;
export const CALL_NUMBER_INVALID_ERROR = "placetel-call-number-invalid";

/** The backend refused the number of a call; no call exists to end. */
export class CallNumberInvalidError extends Error {
  constructor() {
    super(CALL_NUMBER_INVALID_ERROR);
    this.name = "CallNumberInvalidError";
  }
}

const statusForTokenError = (errorCode: PlacetelPhoneTokenError): PhoneStatus => {
  if (errorCode === PlacetelPhoneTokenError.NotConnected || errorCode === PlacetelPhoneTokenError.ReconnectRequired) {
    return { kind: "notConnected" };
  }
  return errorCode === PlacetelPhoneTokenError.Unavailable ? { kind: "unavailable" } : { kind: "error" };
};

const secondsUntil = (expiresAt: string): number =>
  Math.max(0, Math.floor((Date.parse(expiresAt) - Date.now()) / 1000));

type ReadToken = { token: SdkToken; status: null } | { token: null; status: PhoneStatus };

const readToken = (issued: IssuedPhoneToken): ReadToken => {
  if (issued.errorCode) return { token: null, status: statusForTokenError(issued.errorCode) };
  if (!issued.accessToken || !issued.expiresAt || Number.isNaN(Date.parse(issued.expiresAt))) {
    return { token: null, status: { kind: "error" } };
  }
  return {
    token: {
      access_token: issued.accessToken,
      expires_in: secondsUntil(issued.expiresAt),
      refresh_token: PORTAL_MANAGED_REFRESH_TOKEN,
    },
    status: null,
  };
};

const isSdkTokenError = (error: unknown): boolean =>
  typeof error === "object" && error !== null && "type" in error && error.type === SDK_TOKEN_ERROR_TYPE;

// The SDK wraps the browser's error in a plain object, so the browser error name only
// survives inside its message.
const isMicrophoneDenied = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null) return false;
  const name = "name" in error ? error.name : undefined;
  const message = "message" in error ? error.message : undefined;
  return name === "NotAllowedError" || (typeof message === "string" && message.includes("NotAllowedError"));
};

// Where a call stands. "answering" is an incoming call the agent answered: `answerSent` is set once the SDK's
// answer() resolved. "connected" also covers an outgoing call the other side answered;
// "established" means the media flows, and the SDK then ends a failing call itself.
type CallLifecycle =
  | { kind: "new" }
  | { kind: "answering"; answerSent: boolean }
  | { kind: "connected"; established: boolean }
  | { kind: "ended" };

// Why the portal ends a call; without a cause it ends as answered or missed.
type CallEndCause = "failed" | "declined";

const isEnded = (current: { lifecycle: CallLifecycle }): boolean => current.lifecycle.kind === "ended";

// Answered or connected, but the media does not flow yet.
const awaitsMedia = ({ lifecycle }: { lifecycle: CallLifecycle }): boolean =>
  lifecycle.kind === "answering" || (lifecycle.kind === "connected" && !lifecycle.established);

// An answered incoming call whose answer reached the SDK but that is not connected yet: when the SDK ends it
// now, it failed rather than went missed.
const answerFailedToConnect = ({ lifecycle }: { lifecycle: CallLifecycle }): boolean =>
  lifecycle.kind === "answering" && lifecycle.answerSent;

type CallSession = {
  runId: number;
  portalCallId: string;
  direction: PlacetelCallDirection;
  // The call's creation time is kept independently from the UI's connected time.
  createdAt: number;
  answeredAt: number | null;
  /**
   * As recorded with the call. A later caller-id update changes what the panel shows; it fills this number
   * only while the call has none, so a number once recorded never changes.
   */
  number: string | null;
  state: ActiveCall;
  call: CallingCall | null;
  stream: LocalMicrophoneStream | null;
  lifecycle: CallLifecycle;
  // Set before the call ends; endSession derives the outcome from it and the lifecycle.
  endCause: CallEndCause | null;
  // Ends the call when it does not become established in time; armed while the call waits for its media.
  connectTimer: ReturnType<typeof setTimeout> | null;
  // Events of one call reach the backend in order, except the end sent while the page unloads.
  queue: Promise<unknown>;
};

// The SDK would end the call without a word when the stream has no audio track.
const hasNoAudioTrack = (stream: LocalMicrophoneStream): boolean => stream.outputStream.getAudioTracks().length === 0;

type RecordResult = { ok: true; call: RecordedCallEvent } | { ok: false; error: unknown };

const attempt = (action: () => void): void => {
  try {
    action();
  } catch {
    // Best effort: the call is being torn down anyway.
  }
};

const quietly = async (action: () => Promise<void>): Promise<void> => {
  try {
    await action();
  } catch {
    // Best effort: the phone is being torn down anyway.
  }
};

/**
 * Registers the agent's own line with the calling SDK and keeps it signed in.
 * Plain TypeScript: the React provider owns the engine's lifetime.
 * The access token is never logged or stored by the portal; the SDK keeps it in memory.
 */
export const createPhoneEngine = ({
  loadCalling,
  issueToken,
  recordEvent,
  onStatus: reportStatus,
  onCall,
  onCallEnded,
  onConnectFailed,
  onRemoteMedia,
  onReloadRequired,
}: PhoneEngineOptions): PhoneEngine => {
  // Every start and stop moves the run on, so callbacks of an earlier run go quiet.
  let run = 0;
  let line: CallingLine | null = null;
  let sdk: WebexCallingGlobal | null = null;
  let ready = false;
  let session: CallSession | null = null;
  // The line registers again once the running call ended.
  let reRegisterPending = false;
  // The phone account was disconnected during a call: the call goes on and this status is reported,
  // and the phone stopped, once it ended. Reporting it earlier would make the portal shut the phone down.
  let stopAfterCall: PhoneStatus | null = null;
  // A failed token renewal is asked for again a few times; a renewal that worked ends the streak.
  let renewRetryTimer: ReturnType<typeof setTimeout> | null = null;
  let renewRetries = 0;

  // Set once the page unloads: the end of the running call then has to outlive the page.
  let unloading = false;
  // Set once the sign-out starts: the end of the running call has to survive the page reload that follows it.
  let keepalive = false;
  // The queue of the call that ended last, so a shutdown also waits for an end that was sent just before it.
  let lastEndedQueue: Promise<unknown> = Promise.resolve();
  // Ended calls whose queued end event has not started yet, so an unload can send it at once.
  const pendingEndedSessions = new Map<CallSession, PlacetelCallOutcome>();

  const onStatus = (status: PhoneStatus) => {
    ready = status.kind === "ready";
    trace("call", "status", {}, `status=${status.kind}`);
    reportStatus(status);
  };

  const emit = (id: number, status: PhoneStatus) => {
    if (id === run) onStatus(status);
  };

  const fail = (id: number, error: unknown, operation: string) => {
    if (id !== run) return;
    captureException(error, `telephony/phone: ${operation}`);
    onStatus({ kind: "error" });
  };

  const updateCall = (current: CallSession, changes: Partial<ActiveCall>) => {
    current.state = { ...current.state, ...changes };
    if (session === current) onCall(current.state);
  };

  // Any event of the call tells the backend's id; the first one wins.
  const rememberCallId = (current: CallSession, callId: string) => {
    if (current.state.callId === null) updateCall(current, { callId });
  };

  const send = (
    current: CallSession,
    event: PlacetelCallEvent,
    outcome?: PlacetelCallOutcome
  ): Promise<RecordResult> => {
    // An unload sends an end at once rather than wait for earlier events: the page may be gone before they settle.
    // The backend merges the events of a call in any order.
    const immediate = unloading && event === PlacetelCallEvent.Ended;
    const input: CallEventInput = {
      portalCallId: current.portalCallId,
      direction: current.direction,
      remoteNumber: current.number,
      event,
      outcome,
      record: current.state.record ?? undefined,
    };
    if (immediate) {
      input.startedAt = new Date(current.createdAt).toISOString();
      if (outcome === PlacetelCallOutcome.Answered && current.answeredAt !== null) {
        input.answeredAt = new Date(current.answeredAt).toISOString();
      }
    }
    // Every end uses keepalive from its first request; other events use it only while unloading or signing out.
    const post = () => {
      if (event === PlacetelCallEvent.Ended) pendingEndedSessions.delete(current);
      return event === PlacetelCallEvent.Ended || unloading || keepalive
        ? recordEvent(input, { keepalive: true })
        : recordEvent(input);
    };
    const result = (immediate ? Promise.resolve() : current.queue).then(post).then(
      (call): RecordResult => {
        rememberCallId(current, call.id);
        return { ok: true, call };
      },
      (error: unknown): RecordResult => ({ ok: false, error })
    );
    // Events of the call that follow still wait for all of these.
    current.queue = immediate ? Promise.all([current.queue, result]) : result;
    return result;
  };

  // A recording failure never breaks the call.
  const track = (current: CallSession, event: PlacetelCallEvent, outcome?: PlacetelCallOutcome) => {
    void send(current, event, outcome).then(result => {
      if (!result.ok) captureException(result.error, "telephony/phone: record call event");
    });
  };

  const reRegisterLine = async (id: number): Promise<void> => {
    const current = line;
    if (id !== run || !current) return;
    trace("call", "reregister", {});
    try {
      await current.deregister();
      if (id !== run || isTornDown()) return;
      await current.register();
    } catch (error) {
      fail(id, error, "re-register");
    }
  };

  const stopDeferred = (current: CallSession) => {
    if (stopAfterCall === null || current.runId !== run) return;
    const status = stopAfterCall;
    stopAfterCall = null;
    onStatus(status);
    void stop();
  };

  const releaseLine = (current: CallSession) => {
    if (reRegisterPending && current.runId === run) {
      reRegisterPending = false;
      void reRegisterLine(run);
    }
  };

  const traceCall = (current: CallSession, event: string, detail: string) =>
    trace("call", event, { cid: current.portalCallId }, detail);

  const clearConnectTimer = (current: CallSession) => {
    if (current.connectTimer === null) return;
    clearTimeout(current.connectTimer);
    current.connectTimer = null;
  };

  const endSession = (current: CallSession, endSdkCall: boolean) => {
    if (isEnded(current)) return;
    clearConnectTimer(current);
    // The outcome follows the lifecycle the call ended in.
    let outcome = PlacetelCallOutcome.Missed;
    if (current.endCause === "failed") outcome = PlacetelCallOutcome.Failed;
    else if (current.lifecycle.kind === "connected") outcome = PlacetelCallOutcome.Answered;
    else if (current.endCause === "declined") outcome = PlacetelCallOutcome.Rejected;
    current.lifecycle = { kind: "ended" };
    if (session === current) session = null;
    const sdkCall = current.call;
    if (endSdkCall && sdkCall) attempt(() => sdkCall.end());
    const stream = current.stream;
    if (stream) attempt(() => stream.stop());
    current.stream = null;
    onCall(null);
    onRemoteMedia(null);
    const { callId, number, direction, record } = current.state;
    if (callId !== null) onCallEnded({ callId, number, direction, linked: record !== null });
    traceCall(current, "end", `direction=${current.state.direction} outcome=${outcome.toLowerCase()}`);
    pendingEndedSessions.set(current, outcome);
    track(current, PlacetelCallEvent.Ended, outcome);
    lastEndedQueue = current.queue;
    // Stopping first keeps the line from registering again for a phone that is going away.
    stopDeferred(current);
    releaseLine(current);
  };

  const markAnswerSent = (current: CallSession) => {
    if (current.lifecycle.kind === "answering") current.lifecycle = { kind: "answering", answerSent: true };
  };

  // A call the provider never connects would otherwise stay in the panel until the agent hangs up.
  const armConnectTimer = (current: CallSession) => {
    if (current.connectTimer !== null || isEnded(current)) return;
    current.connectTimer = setTimeout(() => {
      current.connectTimer = null;
      if (isEnded(current)) return;
      // A call the other side answered stays answered; an answered incoming call that never connected failed.
      const connected = current.lifecycle.kind === "connected";
      if (!connected) current.endCause = "failed";
      traceCall(current, "timeout", `direction=${current.state.direction} connected=${connected}`);
      endSession(current, true);
      if (!connected) onConnectFailed();
    }, CONNECT_TIMEOUT_MS);
  };

  const clearRenewRetry = () => {
    if (renewRetryTimer !== null) clearTimeout(renewRetryTimer);
    renewRetryTimer = null;
    renewRetries = 0;
  };

  const stop = async (): Promise<void> => {
    run += 1;
    ready = false;
    reRegisterPending = false;
    stopAfterCall = null;
    clearRenewRetry();
    sdk = null;
    setRenewer(null);
    if (session) endSession(session, true);
    const stoppedLine = line;
    line = null;
    if (stoppedLine) await quietly(() => stoppedLine.deregister());
  };

  const shutdown = async (options?: { unloading?: boolean; keepalive?: boolean }): Promise<void> => {
    if (options?.unloading) {
      unloading = true;
      const pendingEnds = Array.from(pendingEndedSessions);
      pendingEndedSessions.clear();
      for (const [current, outcome] of pendingEnds) {
        track(current, PlacetelCallEvent.Ended, outcome);
        lastEndedQueue = current.queue;
      }
    }
    if (options?.keepalive) keepalive = true;
    const instance = getCallingInstance();
    if (instance) markTornDown();
    await stop();
    // The end of the running or just ended call is on its way to the backend; a sign-out must not cancel it.
    // A page that unloads cannot wait: its end was sent at once, with keepalive.
    await Promise.all([
      instance ? quietly(() => instance.deregister()) : undefined,
      options?.unloading ? undefined : lastEndedQueue,
    ]);
  };

  const newSession = (
    id: number,
    portalCallId: string,
    direction: PlacetelCallDirection,
    number: string | null,
    state: ActiveCall
  ): CallSession => ({
    runId: id,
    portalCallId,
    direction,
    createdAt: state.startedAt,
    answeredAt: null,
    number,
    state,
    call: null,
    stream: null,
    lifecycle: { kind: "new" },
    endCause: null,
    connectTimer: null,
    queue: Promise.resolve(),
  });

  const listen = (current: CallSession, sdkCall: CallingCall) => {
    // Outgoing calls get "connect" when the other side answers; an answered incoming call only gets
    // "established". Either connects the call; "established" also means the media flows.
    const onConnected = (established: boolean) => {
      const { lifecycle } = current;
      if (lifecycle.kind === "ended") return;
      if (lifecycle.kind === "connected") {
        if (established) {
          current.lifecycle = { kind: "connected", established: true };
          clearConnectTimer(current);
        }
        return;
      }
      current.lifecycle = { kind: "connected", established };
      traceCall(current, "connect", `direction=${current.state.direction} phase=connected`);
      const answeredAt = Date.now();
      current.answeredAt = answeredAt;
      updateCall(current, { phase: "connected", startedAt: answeredAt });
      track(current, PlacetelCallEvent.Answered);
      if (established) clearConnectTimer(current);
      else armConnectTimer(current);
    };
    sdkCall.on("connect", () => onConnected(false));
    sdkCall.on("established", () => onConnected(true));
    sdkCall.on("remote_media", mediaTrack => {
      if (!isEnded(current)) onRemoteMedia(mediaTrack);
    });
    // The SDK may resolve the caller after the call rang or was answered. The display follows; the logged
    // number stays unless the call had none, which the later events then carry.
    sdkCall.on("caller_id", ({ callerId }) => {
      if (isEnded(current) || (current.state.phase !== "ringing" && current.state.phase !== "connecting")) return;
      if (current.number === null && callerId.num) current.number = callerId.num;
      updateCall(current, {
        number: callerId.num ?? current.state.number,
        name: callerId.name ?? current.state.name,
      });
    });
    // The SDK reports no disconnect for a call the portal ended itself.
    sdkCall.on("disconnect", () => {
      // The SDK clears an answered call that never became established after its own 10 s (or the other side
      // gave up meanwhile): the agent answered it, so it failed rather than went missed. That holds from the
      // moment answer() resolved; a disconnect during the microphone request stays missed.
      const failedToConnect = answerFailedToConnect(current);
      if (failedToConnect) current.endCause = "failed";
      endSession(current, false);
      if (failedToConnect) onConnectFailed();
    });
    sdkCall.on("call_error", () => {
      // The SDK's error stays out of the report; only its occurrence matters.
      captureException(new Error("Phone call error"), "telephony/phone: call error");
      // Once the media flows, the SDK's disconnect ends the call. Before that nothing ends it: the call
      // fails unless the other side answered, which the backend already knows.
      const { lifecycle } = current;
      if (lifecycle.kind === "ended" || (lifecycle.kind === "connected" && lifecycle.established)) return;
      const failedToConnect = answerFailedToConnect(current);
      if (lifecycle.kind !== "connected") current.endCause = "failed";
      endSession(current, true);
      if (failedToConnect) onConnectFailed();
    });
  };

  const handleIncomingCall = (id: number, sdkCall: CallingCall) => {
    if (id !== run) return;
    const caller = sdkCall.getCallerInfo();
    const number = caller.num ?? null;
    const portalCallId = sdkCall.getCorrelationId();
    const state: ActiveCall = {
      direction: "inbound",
      number,
      name: caller.name ?? null,
      phase: "ringing",
      muted: false,
      startedAt: Date.now(),
      callId: null,
      record: null,
    };
    const current = newSession(id, portalCallId, PlacetelCallDirection.Inbound, number, state);
    current.call = sdkCall;
    traceCall(current, "start", "direction=inbound phase=ringing");
    if (session) {
      // There is no call waiting yet: the agent never sees a second call, so it is logged as missed.
      current.lifecycle = { kind: "ended" };
      track(current, PlacetelCallEvent.Started);
      attempt(() => sdkCall.end());
      traceCall(current, "end", `direction=inbound outcome=${PlacetelCallOutcome.Missed.toLowerCase()}`);
      track(current, PlacetelCallEvent.Ended, PlacetelCallOutcome.Missed);
      return;
    }
    session = current;
    listen(current, sdkCall);
    onCall(current.state);
    track(current, PlacetelCallEvent.Started);
  };

  // A running call keeps the line: it registers again after the call.
  const requestReRegister = (id: number) => {
    if (id !== run) return;
    if (session) {
      reRegisterPending = true;
      return;
    }
    void reRegisterLine(id);
  };

  const subscribe = (id: number, registeredLine: CallingLine) => {
    // A token error is retried once per registration; a second one means the phone is not reachable.
    let tokenErrorRetried = false;
    registeredLine.on("registered", () => {
      tokenErrorRetried = false;
      emit(id, { kind: "ready" });
    });
    // The SDK reconnects a line by itself, so this is a pause, not a failure.
    registeredLine.on("unregistered", () => emit(id, { kind: "starting" }));
    registeredLine.on("line:incoming_call", sdkCall => handleIncomingCall(id, sdkCall));
    registeredLine.on("error", error => {
      if (isSdkTokenError(error) && !tokenErrorRetried) {
        tokenErrorRetried = true;
        requestReRegister(id);
        return;
      }
      fail(id, new Error(isSdkTokenError(error) ? "Phone line token error" : "Phone line error"), "line");
    });
  };

  const firstLineOf = (instance: CallingInstance): CallingLine | undefined =>
    instance.callingClient ? Object.values(instance.callingClient.getLines())[0] : undefined;

  const registerLine = async (id: number, instance: CallingInstance): Promise<void> => {
    try {
      // A paused phone keeps the instance signed in; only its line registers again.
      if (!isCallingRegistered()) {
        if (id !== run || isTornDown()) return;
        await instance.register();
        // The phone shut down meanwhile: the register() that just finished must not stay registered.
        if (isTornDown()) {
          await quietly(() => instance.deregister());
          return;
        }
        if (firstLineOf(instance)) markCallingRegistered();
      }
      if (id !== run || isTornDown()) return;
      const firstLine = firstLineOf(instance);
      if (!firstLine) {
        fail(id, new Error("Phone registered without a line"), "register");
        return;
      }
      line = firstLine;
      subscribe(id, firstLine);
      await firstLine.register();
    } catch (error) {
      fail(id, error, "register");
    }
  };

  // The SDK does not renew again after a failed renewal until a request gets a 401 answer. One retry is
  // pending at a time, and it asks the SDK to renew through its own credentials, which calls renewToken again.
  const scheduleRenewRetry = (id: number) => {
    if (renewRetryTimer !== null) return;
    if (renewRetries >= RENEW_RETRY_LIMIT) {
      trace("call", "renew-retry-exhausted", {}, `attempts=${renewRetries}`);
      return;
    }
    if (!getCallingInstance()?.webex?.credentials) return;
    renewRetries += 1;
    renewRetryTimer = setTimeout(() => {
      renewRetryTimer = null;
      const credentials = getCallingInstance()?.webex?.credentials;
      if (id !== run || !credentials) return;
      trace("call", "renew-retry", {}, `attempt=${renewRetries}`);
      // A failure schedules the next retry from renewToken; here it needs no second report.
      void quietly(async () => {
        await credentials.refresh();
      });
    }, RENEW_RETRY_MS);
  };

  // The SDK keeps the token it captured when the line registers, so the line registers again
  // once the SDK has taken the new token.
  const renewToken = async (id: number): Promise<SdkToken> => {
    let retryable = true;
    try {
      if (id !== run) throw new Error("Phone is stopped");
      const renewed = readToken(await issueToken({ renew: true }));
      if (id !== run) throw new Error("Phone is stopped");
      if (renewed.token === null) {
        if (renewed.status.kind === "notConnected") {
          retryable = false;
          if (session) {
            // A running call is not ended by the account: the phone stops after it.
            stopAfterCall = renewed.status;
            trace("call", "stop-deferred", {}, `status=${renewed.status.kind}`);
            clearRenewRetry();
          } else {
            onStatus(renewed.status);
            void stop();
          }
        }
        // Any other outcome is thrown: the SDK does not renew again by itself, the engine retries.
        throw new Error("Phone access could not be renewed");
      }
      // The SDK refuses an access token it already holds and the line stays as it is: that counts as a
      // failed renewal. Only a different access token reaches the SDK and registers the line again.
      const renewedDigest = await digestToken(renewed.token.access_token);
      if (id !== run) throw new Error("Phone is stopped");
      if (isHeldTokenDigest(renewedDigest)) throw new Error("Phone access was not renewed");
      holdTokenDigest(renewedDigest);
      clearRenewRetry();
      setTimeout(() => requestReRegister(id), 0);
      return renewed.token;
    } catch (error) {
      if (retryable && id === run) scheduleRenewRetry(id);
      throw error;
    }
  };

  const start = async (): Promise<void> => {
    if (isTornDown()) {
      onReloadRequired();
      return;
    }
    const stopping = stop();
    const id = run;
    await stopping;
    onStatus({ kind: "starting" });
    try {
      const first = readToken(await issueToken({ renew: false }));
      if (id !== run) return;
      if (first.token === null) {
        onStatus(first.status);
        return;
      }
      const firstToken = first.token;
      const firstDigest = await digestToken(firstToken.access_token);
      if (id !== run) return;
      setRenewer(() => renewToken(id));

      const Calling = await loadCalling();
      if (id !== run) return;
      sdk = Calling;
      // One SDK instance serves the whole page: a later start only registers its line again.
      const instance = await initCallingOnce(async () => {
        const created = await Calling.init({
          webexConfig: {
            config: { logger: { level: "error" }, credentials: { refreshCallback } },
            credentials: firstToken,
          },
          callingConfig: {
            clientConfig: { calling: true, contact: false, callHistory: false, callSettings: false, voicemail: false },
            callingClientConfig: { logger: { level: "error" } },
            logger: { level: "error" },
          },
        });
        holdTokenDigest(firstDigest);
        return created;
      });
      if (id !== run) return;
      // The SDK may be ready before the portal can subscribe, and "ready" may fire more than once.
      let registering = false;
      const registerOnce = () => {
        if (registering) return;
        registering = true;
        void registerLine(id, instance);
      };
      if (isCallingRegistered()) {
        registerOnce();
      } else {
        instance.on("ready", registerOnce);
        if (instance.webex?.ready) registerOnce();
      }
    } catch (error) {
      fail(id, error, "start");
    }
  };

  const retry = async (): Promise<void> => {
    await stop();
    await start();
  };

  const dial = async (number: string, record?: DialRecord): Promise<DialResult> => {
    const id = run;
    const dialLine = line;
    const dialSdk = sdk;
    if (session) return { ok: false, reason: "inCall" };
    if (!ready || !dialLine || !dialSdk) return { ok: false, reason: "notReady" };

    const current = newSession(id, crypto.randomUUID(), PlacetelCallDirection.Outbound, number, {
      direction: "outbound",
      number,
      name: null,
      phase: "dialing",
      muted: false,
      startedAt: Date.now(),
      callId: null,
      record: record ?? null,
    });
    session = current;
    traceCall(current, "start", "direction=outbound phase=dialing");
    onCall(current.state);

    const started = await send(current, PlacetelCallEvent.Started);
    if (isEnded(current)) return { ok: false, reason: "cancelled" };
    let address = number;
    if (started.ok) {
      address = started.call.remoteNumber ?? number;
    } else if (started.error instanceof CallNumberInvalidError) {
      // The backend refused the number, so no call exists to end.
      current.lifecycle = { kind: "ended" };
      session = null;
      traceCall(current, "end", "direction=outbound outcome=refused");
      onCall(null);
      stopDeferred(current);
      releaseLine(current);
      return { ok: false, reason: "invalidNumber" };
    } else {
      captureException(started.error, "telephony/phone: record call event");
    }

    let stream: LocalMicrophoneStream;
    try {
      stream = await dialSdk.createMicrophoneStream();
    } catch (error) {
      if (isEnded(current)) return { ok: false, reason: "cancelled" };
      current.endCause = "failed";
      endSession(current, false);
      if (isMicrophoneDenied(error)) return { ok: false, reason: "microphone" };
      captureException(error, "telephony/phone: microphone");
      return { ok: false, reason: "failed" };
    }
    if (isEnded(current)) {
      attempt(() => stream.stop());
      return { ok: false, reason: "cancelled" };
    }
    current.stream = stream;
    if (hasNoAudioTrack(stream)) {
      captureException(new Error("Microphone stream has no audio track"), "telephony/phone: microphone");
      current.endCause = "failed";
      endSession(current, false);
      return { ok: false, reason: "failed" };
    }

    try {
      const sdkCall = dialLine.makeCall({ type: "uri", address });
      if (!sdkCall) {
        current.endCause = "failed";
        endSession(current, false);
        return { ok: false, reason: "failed" };
      }
      current.call = sdkCall;
      listen(current, sdkCall);
      // The SDK's dial is async: a failure after this point still ends the call.
      void sdkCall.dial(stream).catch((error: unknown) => {
        captureException(error, "telephony/phone: dial");
        current.endCause = "failed";
        endSession(current, true);
      });
    } catch (error) {
      captureException(error, "telephony/phone: dial");
      current.endCause = "failed";
      endSession(current, true);
      return { ok: false, reason: "failed" };
    }
    return { ok: true };
  };

  const answer = async (): Promise<AnswerResult> => {
    const current = session;
    const answerSdk = sdk;
    const sdkCall = current?.call;
    if (
      !current ||
      !sdkCall ||
      !answerSdk ||
      current.direction !== PlacetelCallDirection.Inbound ||
      current.state.phase !== "ringing" ||
      current.lifecycle.kind === "answering"
    ) {
      return { ok: false, reason: "noCall" };
    }
    current.lifecycle = { kind: "answering", answerSent: false };
    traceCall(current, "answer", "direction=inbound phase=connecting");
    updateCall(current, { phase: "connecting" });

    let stream: LocalMicrophoneStream;
    try {
      stream = await answerSdk.createMicrophoneStream();
    } catch (error) {
      if (isEnded(current)) return { ok: false, reason: "cancelled" };
      current.endCause = "failed";
      endSession(current, true);
      if (isMicrophoneDenied(error)) return { ok: false, reason: "microphone" };
      captureException(error, "telephony/phone: microphone");
      return { ok: false, reason: "failed" };
    }
    if (isEnded(current)) {
      attempt(() => stream.stop());
      return { ok: false, reason: "cancelled" };
    }
    current.stream = stream;
    if (hasNoAudioTrack(stream)) {
      captureException(new Error("Microphone stream has no audio track"), "telephony/phone: microphone");
      current.endCause = "failed";
      endSession(current, true);
      return { ok: false, reason: "failed" };
    }

    try {
      await sdkCall.answer(stream);
    } catch (error) {
      captureException(error, "telephony/phone: answer");
      current.endCause = "failed";
      endSession(current, true);
      return { ok: false, reason: "failed" };
    }
    // The SDK resolves answer() before the media flows; "established" is still to come.
    markAnswerSent(current);
    if (awaitsMedia(current)) armConnectTimer(current);
    return { ok: true };
  };

  const isRinging = (current: CallSession): boolean =>
    current.direction === PlacetelCallDirection.Inbound && current.state.phase === "ringing";

  const decline = () => {
    const current = session;
    if (!current || !isRinging(current)) return;
    current.endCause = "declined";
    endSession(current, true);
  };

  // Hanging up a call that still rings, or that was answered but did not connect yet, is declining it.
  const hangUp = () => {
    const current = session;
    if (!current) return;
    if (isRinging(current) || current.lifecycle.kind === "answering") current.endCause = "declined";
    endSession(current, true);
  };

  const setMuted = (muted: boolean) => {
    const current = session;
    if (!current?.stream) return;
    current.stream.setUserMuted(muted);
    updateCall(current, { muted });
  };

  const linkRecord = (record: DialRecord) => {
    const current = session;
    if (!current) return;
    updateCall(current, { record });
    // The backend links the call to the record of any event that carries one; later events carry it by themselves.
    if (current.lifecycle.kind === "connected") track(current, PlacetelCallEvent.Answered);
  };

  return { start, stop, retry, shutdown, dial, answer, decline, hangUp, setMuted, linkRecord };
};
